import { createContext, useEffect, useReducer, useState } from "react";

import { database, auth } from "../config/firebaseConfig";

const INITIALIZE = "INITIALIZE";
const CACHE_KEY = "authUserDetail";

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const reducer = (state, action) => {
  if (action.type === INITIALIZE) {
    const { isAuthenticated, user } = action.payload;
    const updatedState = {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };

    return updatedState;
  }

  return state;
};

const AuthContext = createContext(null);

function AuthProvider({ children }) {
  const [profile, setProfile] = useState();
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    let profileRef = null;
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (profileRef) {
        profileRef.off();
        profileRef = null;
      }
      if (user) {
        // Seed the role from the cached profile so a page refresh doesn't
        // treat an admin as GUEST while the live profile loads (role-gated
        // routes would otherwise bounce to 401 during that window).
        try {
          const cached = JSON.parse(window.localStorage.getItem(CACHE_KEY));
          if (cached && cached.uid === user.uid) {
            setProfile(cached);
          }
        } catch (e) {
          // ignore unreadable cache
        }

        profileRef = database.ref("users/users").child(user.uid);
        profileRef.on("value", (snapshot) => {
          const dbProfile = snapshot.val() || null;
          if (dbProfile) {
            window.localStorage.setItem(CACHE_KEY, JSON.stringify(dbProfile));
          }
          setProfile(dbProfile);
        });

        dispatch({
          type: INITIALIZE,
          payload: { isAuthenticated: true, user },
        });
      } else {
        window.localStorage.removeItem(CACHE_KEY);
        setProfile(undefined);
        dispatch({
          type: INITIALIZE,
          payload: { isAuthenticated: false, user: null },
        });
      }
    });
    return () => {
      unsubscribe();
      if (profileRef) profileRef.off();
    };
  }, [dispatch]);

  const signIn = (email, password) =>
    auth.signInWithEmailAndPassword(email, password);

  const signUp = (email, password, firstName, lastName, onError) =>
    auth
      .createUserWithEmailAndPassword(email, password)
      .then((res) => {
        res.user.sendEmailVerification();
        database.ref("users/users").child(res.user?.uid).update({
          uid: res.user?.uid,
          email,
          firstName,
          lastName,
          // role: ["GUEST"],
        });
        return res.user;
      })
      .catch(onError);

  const signOut = async () => {
    await auth.signOut();
  };

  const resetPassword = async (email) => {
    await auth.sendPasswordResetEmail(email);
  };

  const authUser = { ...state.user };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "firebase",
        // undefined = profile (and role) still loading; null/object = loaded
        isProfileLoaded: profile !== undefined,
        user: {
          id: authUser.uid,
          uid: authUser.uid,
          email: authUser.email,
          avatar: authUser.avatar || profile?.avatar,
          firstName: authUser.firstName || profile?.firstName,
          lastName: authUser.lastName || profile?.lastName,
          role: profile?.role || "GUEST",
          isAdmin: profile?.role && profile.role.includes("ADMIN"),
        },
        signIn,
        signUp,
        signOut,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
